import { useEffect, useRef, useState } from "react";
import {
  BarChart, Bar, XAxis, ResponsiveContainer, Cell, LabelList,
} from "recharts";
import {
  Clapperboard, Music4, MessageSquareText, Users2, LineChart as LineChartIcon,
  Play, Pause, Loader2, ChevronRight, FileText, CheckCircle2, Circle, SlidersHorizontal,
} from "lucide-react";
import { api } from "@/api/client";
import { socialApi } from "@/api/social";
import { useSession } from "@/context/SessionContext";
import { USER_ID } from "@/constants/hubs";

const CORAL = "#FF9E9E";
const TRACK_LABEL = { image: "Image", video: "Video", music: "Music", writing: "Writing" };

export const DECK_OPTIONS = [
  { mode: "reels", label: "Live Reels", Icon: Clapperboard },
  { mode: "music", label: "Mist Music", Icon: Music4 },
  { mode: "chat", label: "Chat Ticker", Icon: MessageSquareText },
  { mode: "work", label: "Teams", Icon: Users2 },
  { mode: "portfolio", label: "Portfolio", Icon: LineChartIcon },
];
const DECK_MAP = Object.fromEntries(DECK_OPTIONS.map((d) => [d.mode, d]));

function Frame({ title, Icon, children, testid, onSettings }) {
  return (
    <div
      className="border-4 border-charcoal rounded-3xl bg-white overflow-hidden shadow-hard"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 px-3.5 py-2 border-b-4 border-charcoal bg-charcoal">
        <Icon size={14} strokeWidth={2.4} className="text-gold" />
        <p className="font-mono text-[9px] font-black uppercase tracking-[0.22em] text-white flex-1 truncate">{title}</p>
        {onSettings && (
          <button onClick={onSettings} data-testid="deck-settings-btn" aria-label="Swap deck"
            className="flex items-center gap-1 rounded-full border border-gold/60 px-2 py-0.5 active:opacity-70">
            <SlidersHorizontal size={11} className="text-gold" />
            <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-gold/80">Swap</span>
          </button>
        )}
      </div>
      <div className="h-[150px]">{children}</div>
    </div>
  );
}

/* ------------------------------ REELS DECK ------------------------------ */
function ReelsDeck({ onNavigate }) {
  const [reel, setReel] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let url;
    (async () => {
      try {
        const { data } = await api.post("/central-core/query_vault", { op: "stream_feed", params: {} });
        const first = (data.reels || [])[0];
        if (first) {
          const res = await api.get(`/central-core/vault-file/${first.id}`, { responseType: "blob" });
          url = URL.createObjectURL(res.data);
          setReel({ ...first, src: url });
        }
      } catch { /* offline */ } finally { setLoading(false); }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, []);

  return (
    <button
      onClick={() => onNavigate?.("reels")}
      data-testid="deck-reels"
      className="relative w-full h-full bg-black grid place-items-center active:opacity-90"
    >
      {loading ? (
        <Loader2 size={20} className="text-gold animate-spin" />
      ) : reel?.src ? (
        <>
          <video src={reel.src} muted autoPlay loop playsInline className="w-full h-full object-cover" data-testid="deck-reels-video" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute left-3 bottom-2.5 right-3 text-left">
            <p className="font-mono text-[10px] font-black text-white truncate">@{reel.handle}</p>
            <p className="font-mono text-[9px] text-white/70 truncate">{reel.title}</p>
          </div>
        </>
      ) : (
        <div className="text-center px-4">
          <Clapperboard size={26} className="text-gold mx-auto" />
          <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/70">No reels yet</p>
          <p className="font-mono text-[9px] text-gold mt-0.5">Tap to explore →</p>
        </div>
      )}
    </button>
  );
}

/* ------------------------------ MUSIC DECK ------------------------------ */
function MusicDeck() {
  const [count, setCount] = useState(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    api.post("/central-core/query_vault", { op: "list", params: { track: "music" } })
      .then((r) => setCount(r.data.count ?? 0)).catch(() => setCount(0));
  }, []);
  return (
    <div className="w-full h-full bg-cream flex flex-col justify-between p-3.5" data-testid="deck-music">
      <div className="flex items-center gap-1 h-12">
        {Array.from({ length: 22 }).map((_, i) => (
          <span key={i}
            className="flex-1 rounded-full bg-charcoal"
            style={{
              height: playing ? `${20 + Math.abs(Math.sin(i * 1.7)) * 78}%` : "22%",
              transition: "height .35s ease",
              animation: playing ? `deckEq 900ms ${i * 40}ms ease-in-out infinite alternate` : "none",
              opacity: playing ? 1 : 0.45,
              transformOrigin: "bottom",
            }} />
        ))}
      </div>
      <div className="flex items-center gap-3">
        <button onClick={() => setPlaying((p) => !p)} data-testid="deck-music-play"
          className="w-11 h-11 rounded-full bg-gold border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none shrink-0">
          {playing ? <Pause size={18} className="text-charcoal" /> : <Play size={18} className="text-charcoal ml-0.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-base text-charcoal leading-none truncate">Mist Library Stream</p>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-charcoal/50 mt-1">
            {count == null ? "loading…" : `${count} track${count === 1 ? "" : "s"} · personal + shared`}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ CHAT DECK ------------------------------- */
function ChatDeck({ onNavigate }) {
  const [items, setItems] = useState(null);
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    socialApi.notifications()
      .then((r) => { setItems(r.notifications || []); setUnread(r.unread || 0); })
      .catch(() => setItems([]));
  }, []);
  const LABEL = { FRIEND_REQ: "sent a friend request", FRIEND_ACCEPT: "accepted your request" };
  return (
    <button onClick={() => onNavigate?.("chat")} data-testid="deck-chat"
      className="w-full h-full bg-cream flex flex-col active:opacity-95 text-left">
      <div className="flex items-center gap-2 px-3.5 pt-3">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
        <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-charcoal flex-1">Comms Queue</p>
        <span className="font-mono text-[9px] font-black text-charcoal bg-gold border-2 border-charcoal rounded-full px-2 py-0.5">{unread} new</span>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden px-3.5 py-2 space-y-1.5">
        {items == null ? (
          <div className="h-full grid place-items-center"><Loader2 size={16} className="text-charcoal/40 animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="font-mono text-[10px] text-charcoal/45 pt-3">No incoming communications.</p>
        ) : items.slice(0, 3).map((n) => (
          <div key={n.id} className="flex items-center gap-2 bg-white border-2 border-charcoal rounded-lg px-2 py-1 shadow-[2px_2px_0px_#1A1A1A]">
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${n.unread ? "bg-red-500" : "bg-charcoal/25"}`} />
            <p className="font-mono text-[9.5px] text-charcoal truncate">
              <b>{n.actor_name}</b> {LABEL[n.type] || "sent an update"}
            </p>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end gap-1 px-3.5 pb-2 font-mono text-[9px] uppercase tracking-widest text-gold">
        Open Chat <ChevronRight size={12} />
      </div>
    </button>
  );
}

/* ------------------------------ WORK DECK ------------------------------- */
const WORK_TASKS = [
  { t: "Deploy: reels render worker", done: true, file: "render_worker.py" },
  { t: "Review: gateway OTP copy", done: false, file: "Gateway.jsx" },
  { t: "Log: vault storage audit", done: false, file: "vault_core.py" },
];
function WorkDeck({ onNavigate }) {
  return (
    <button onClick={() => onNavigate?.("chat")} data-testid="deck-work"
      className="w-full h-full bg-cream flex flex-col p-3 text-left active:opacity-95">
      <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-charcoal/60 mb-1.5">Team Workspace · submission log</p>
      <div className="flex-1 min-h-0 overflow-hidden space-y-1.5">
        {WORK_TASKS.map((w) => (
          <div key={w.t} className="flex items-center gap-2 bg-white border-2 border-charcoal rounded-lg px-2 py-1.5 shadow-[2px_2px_0px_#1A1A1A]">
            {w.done ? <CheckCircle2 size={13} className="text-emerald-500 shrink-0" /> : <Circle size={13} className="text-charcoal/40 shrink-0" />}
            <p className={`font-mono text-[9.5px] flex-1 truncate ${w.done ? "line-through text-charcoal/40" : "text-charcoal"}`}>{w.t}</p>
            <span className="flex items-center gap-0.5 font-mono text-[8px] text-charcoal/50 shrink-0"><FileText size={9} />{w.file}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end gap-1 pt-1 font-mono text-[9px] uppercase tracking-widest text-gold">
        Open Team Chat <ChevronRight size={12} />
      </div>
    </button>
  );
}

/* ---------------------------- PORTFOLIO DECK ---------------------------- */
function PortfolioDeck() {
  const { user } = useSession();
  const uid = user?.user_id || USER_ID;
  const [chart, setChart] = useState(null);
  useEffect(() => {
    const f = () => api.get(`/account/charts/${uid}`).then((r) => setChart(r.data)).catch(() => {});
    f();
    const id = setInterval(f, 5000);
    return () => clearInterval(id);
  }, [uid]);
  const data = (chart?.series || []).map((s) => ({ name: TRACK_LABEL[s.track] || s.track, count: s.count, track: s.track }));
  return (
    <div className="w-full h-full bg-white px-2 pt-2" data-testid="deck-portfolio">
      <ResponsiveContainer width="100%" height={140} minHeight={140}>
        <BarChart data={data} margin={{ top: 18, right: 4, left: 4, bottom: 0 }}>
          <XAxis dataKey="name" axisLine={{ stroke: "#1A1A1A" }} tickLine={false} tick={{ fill: "#1A1A1A", fontFamily: "monospace", fontSize: 10 }} />
          <Bar dataKey="count" radius={[8, 8, 0, 0]} isAnimationActive>
            {data.map((d) => <Cell key={d.track} fill={d.track === "video" ? CORAL : "#1A1A1A"} />)}
            <LabelList dataKey="count" position="top" style={{ fill: "#1A1A1A", fontFamily: "monospace", fontSize: 12, fontWeight: 700 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const TITLE = {
  reels: "Live Reels Player", music: "Mist Music Player", chat: "Active Chat Ticker",
  work: "Teams Workspace", portfolio: "Creator Portfolio",
};

export default function TopDeck({ mode = "reels", onNavigate, onSettings }) {
  const m = DECK_MAP[mode] ? mode : "reels";
  const Icon = DECK_MAP[m].Icon;
  return (
    <Frame title={TITLE[m]} Icon={Icon} testid="top-deck" onSettings={onSettings}>
      {m === "reels" && <ReelsDeck onNavigate={onNavigate} />}
      {m === "music" && <MusicDeck />}
      {m === "chat" && <ChatDeck onNavigate={onNavigate} />}
      {m === "work" && <WorkDeck onNavigate={onNavigate} />}
      {m === "portfolio" && <PortfolioDeck />}
    </Frame>
  );
}
